import { useUpdateAdminUserMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

interface UserData {
	id: string;
	firstName: string;
	lastName: string;
	email?: string;
}

export function EditUserModal({
	open,
	onOpenChange,
	user,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	user: UserData | null;
	onSuccess?: () => void;
}) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');

	const [updateAdminUser, { loading }] = useUpdateAdminUserMutation();

	useEffect(() => {
		if (open && user) {
			setFirstName(user.firstName);
			setLastName(user.lastName);
		}
	}, [open, user]);

	const handleSubmit = async () => {
		if (!user || !firstName.trim() || !lastName.trim()) return;

		try {
			const { data } = await updateAdminUser({
				variables: {
					id: user.id,
					input: {
						firstName: firstName.trim(),
						lastName: lastName.trim(),
					},
				},
			});

			if (data?.updateAdminUser?.success) {
				toast.success('User updated');
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.updateAdminUser?.message || 'Failed to update user');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update user');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit user</DialogTitle>
					<DialogDescription>Update user information.</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{user?.email && (
								<div className="flex flex-col gap-1.5">
									<Label>Email</Label>
									<Input value={user.email} disabled />
								</div>
							)}

							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label>
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="First name"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label>
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Last name"
									/>
								</div>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!firstName.trim() || !lastName.trim() || loading}
						>
							{loading ? 'Saving…' : 'Save changes'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
